import { useId } from 'react';
import { Info, Trophy, X, Check } from 'lucide-react';
import { useT } from '../../i18n';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';
import type { Player } from '../../types/models';
import { MatchupLines } from './MatchupLines';

/** A team slot referencing a concrete player or a placeholder. */
interface TeamSlot {
  kind: string;
  playerId?: string;
  [key: string]: any;
}

/** A match's two teams plus court/phase metadata. */
interface ConfirmResultMatch {
  team1: { p1: TeamSlot; p2: TeamSlot; [key: string]: any };
  team2: { p1: TeamSlot; p2: TeamSlot; [key: string]: any };
  court?: number | string;
  phase?: string | number;
  round?: number | string;
  [key: string]: any;
}

/** The pending result awaiting confirmation: the match and both scores. */
export interface ConfirmResultPayload {
  match: ConfirmResultMatch;
  s1: number;
  s2: number;
}

export interface ConfirmResultModalProps {
  /** The match plus the two scores being confirmed. */
  payload: ConfirmResultPayload;
  /** Full player roster, used to resolve names in the matchup. */
  players?: Player[];
  /** Called when the user confirms and sends the result. */
  onConfirm: () => void;
  /** Called when the user cancels (button or backdrop click). */
  onCancel: () => void;
}

/** Confirmation modal shown before sending a match result. Extracted verbatim from legacy/App.tsx; markup unchanged. */
export function ConfirmResultModal({
  payload,
  players,
  onConfirm,
  onCancel,
}: ConfirmResultModalProps) {
  const tr = useT();
  // A `Dialog` (#900): named by its title, described by the court/phase line
  // above it, Tab kept inside, Escape and the backdrop cancel as «Avbryt»
  // does, and focus goes back to «Send resultat». Styles moved across verbatim.
  const uid = safeId('confirm-result', useId());
  const titleId = `${uid}-title`;
  const contextId = `${uid}-context`;
  const { match, s1, s2 } = payload;
  const t1Won = s1 > s2;
  const t2Won = s2 > s1;
  const isDraw = s1 === s2;

  // Work out the winner line.
  const getTeamPlayers = (team) => {
    const p1 = team.p1.kind === 'concrete' ? players?.find((p) => p.id === team.p1.playerId) : null;
    const p2 = team.p2.kind === 'concrete' ? players?.find((p) => p.id === team.p2.playerId) : null;
    return [p1, p2]
      .filter(Boolean)
      .map((p) => p.name.split(' ')[0])
      .join(' + ');
  };

  const team1Names = getTeamPlayers(match.team1);
  const team2Names = getTeamPlayers(match.team2);
  const winnerNames = t1Won ? team1Names : t2Won ? team2Names : null;

  return (
    <Dialog
      labelledBy={titleId}
      describedBy={contextId}
      onClose={onCancel}
      showClose={false}
      backdropClassName=""
      backdropStyle={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background: 'rgba(15, 22, 17, 0.65)',
        backdropFilter: 'blur(4px)',
      }}
      className="overflow-x-hidden overflow-y-auto max-h-[92vh]"
      style={{
        maxHeight: 'calc(100dvh - 2rem)',
        background: '#ffffff',
        maxWidth: 460,
        width: '100%',
        borderRadius: 20,
        boxShadow: '0 25px 60px -10px rgba(0,0,0,0.4)',
        border: '1px solid #e7eae3',
      }}
    >
      {/* Header */}
      <div style={{ padding: '18px 22px', borderBottom: '1px solid #e7eae3' }}>
        <div
          id={contextId}
          className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1"
        >
          {match.phase === 'finals'
            ? tr('tournament.confirm.headerFinals', { court: String(match.court) })
            : match.round
              ? tr('tournament.confirm.headerPhaseRound', {
                  court: String(match.court),
                  phase: String(match.phase),
                  round: String(match.round),
                })
              : tr('tournament.confirm.headerPhase', {
                  court: String(match.court),
                  phase: String(match.phase),
                })}
        </div>
        <h3 id={titleId} className="font-display text-xl font-semibold text-ink">
          {tr('tournament.confirm.title')}
        </h3>
      </div>

      {/* Innhold — matchup med score */}
      <div style={{ padding: '20px 22px' }}>
        <MatchupLines
          team1={match.team1}
          team2={match.team2}
          players={players}
          light
          score={{ s1, s2 }}
        />

        {/* Vinner-banner */}
        <div
          className="mt-4 p-3 rounded-lg flex items-center gap-2"
          style={{
            background: isDraw ? '#f0f2ec' : '#dcfce7',
            border: `1px solid ${isDraw ? '#d4d8cf' : '#86d96b'}`,
          }}
        >
          {isDraw ? (
            <>
              <Info size={16} style={{ color: '#3a4a40' }} />
              <span className="text-sm font-medium" style={{ color: '#3a4a40' }}>
                {tr('tournament.confirm.draw', { s1, s2 })}
              </span>
            </>
          ) : (
            <>
              <Trophy size={16} style={{ color: '#3f6212' }} />
              <span className="text-sm" style={{ color: '#3f6212' }}>
                {tr('tournament.confirm.winnerLabel')}{' '}
                <span className="font-bold">{winnerNames}</span> ({Math.max(s1, s2)}–
                {Math.min(s1, s2)})
              </span>
            </>
          )}
        </div>

        <p className="text-xs text-ink-3 mt-3 leading-relaxed">
          {tr('tournament.confirm.explainer')}
        </p>
      </div>

      {/* Footer-knapper */}
      <div
        style={{
          padding: '14px 22px',
          borderTop: '1px solid #e7eae3',
          background: '#fafbf7',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <button type="button" onClick={onCancel} className="btn-ghost">
          <X size={14} /> {tr('tournament.confirm.cancel')}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="px-5 py-2 rounded-lg font-mono text-xs font-bold uppercase tracking-wider transition-all hover:brightness-110 active:scale-95 flex items-center gap-1.5"
          style={{ background: '#0f1611', color: '#c2f02f' }}
        >
          <Check size={14} strokeWidth={3} /> {tr('tournament.confirm.submit')}
        </button>
      </div>
    </Dialog>
  );
}
